import { fetchChessComGames, outcomeFor } from './api';
import { parseSingleGame } from '../pgn/parse';
import { upsertGame } from '../storage/db';
import type { ChessComSettings } from './settings';
import type { Color } from '../../types';

export interface SyncResult {
  importedIds: string[];
  failed: number;
  newestEndTime?: number;
}

/** Première synchro (aucun `lastSyncEndTime` connu) : on ne remonte pas tout
 * l'historique disponible, seulement les toutes dernières parties. */
const FIRST_SYNC_LIMIT = 5;

/**
 * Récupère les parties Chess.com récentes qui passent le filtre de cadence,
 * et enregistre celles qui n'ont pas encore été synchronisées (dédoublonnage
 * par id de partie, via `upsertGame`). N'analyse rien elle-même — c'est au
 * appelant de mettre en file les ids retournés (cf. hooks/useChessComAutoSync).
 */
export async function syncChessCom(settings: ChessComSettings): Promise<SyncResult> {
  const name = settings.username.trim().toLowerCase();
  if (!name) return { importedIds: [], failed: 0, newestEndTime: settings.lastSyncEndTime };

  const games = await fetchChessComGames(name, { months: 3, limit: 60 });
  const filtered = games.filter((g) => settings.timeClasses.includes(g.timeClass));
  const isFirstSync = !settings.lastSyncEndTime;
  const toImport = isFirstSync
    ? filtered.slice(0, FIRST_SYNC_LIMIT)
    : filtered.filter((g) => g.endTime > (settings.lastSyncEndTime as number));

  const importedIds: string[] = [];
  let failed = 0;
  let newestEndTime = settings.lastSyncEndTime;

  for (const g of toImport) {
    try {
      const parsed = parseSingleGame(g.pgn, 0);
      const color: Color = g.white.username.toLowerCase() === name ? 'w' : 'b';
      await upsertGame({
        game: parsed,
        importedAt: new Date().toISOString(),
        pgnRaw: g.pgn,
        playerColor: color,
        source: 'chesscom',
        chessCom: {
          url: g.url,
          endTime: g.endTime,
          timeClass: g.timeClass,
          playerRating: color === 'w' ? g.white.rating : g.black.rating,
          opponent: color === 'w' ? g.black.username : g.white.username,
          opponentRating: color === 'w' ? g.black.rating : g.white.rating,
          outcome: outcomeFor(color === 'w' ? g.white.result : g.black.result),
        },
      });
      importedIds.push(parsed.id);
      if (!newestEndTime || g.endTime > newestEndTime) newestEndTime = g.endTime;
    } catch {
      // PGN illisible ou erreur d'enregistrement pour cette partie précise :
      // on continue avec les autres plutôt que d'interrompre toute la synchro.
      failed += 1;
    }
  }

  return { importedIds, failed, newestEndTime };
}
